import { useState, useEffect } from "react";
import { useThemeContext } from "../theme/ThemeContext";
import { Button } from "./shared";
import { getSession, setSession } from "../utils/storageUtils";

const SZ = 36;

export function IOSInstallBanner({ saLogoData, onDismiss }) {
  const { SA, TYPOGRAPHY, RADIUS } = useThemeContext();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const isIOS         = /iphone|ipad|ipod/i.test(navigator.userAgent);
    const isInStandalone = window.navigator.standalone === true;
    const dismissed     = getSession("sa_pwa_banner_dismissed");
    if (isIOS && !isInStandalone && !dismissed) {
      const t = setTimeout(() => setVisible(true), 2500);
      return () => clearTimeout(t);
    }
  }, []);

  const handleDismiss = () => {
    setSession("sa_pwa_banner_dismissed", "1");
    setVisible(false);
    if (onDismiss) onDismiss();
  };

  if (!visible) return null;

  const icons = {
    dots: (
      <svg width={SZ} height={SZ} viewBox="0 0 44 44" fill="none">
        <circle cx="22" cy="22" r="22" fill="#2A2E3D"/>
        <circle cx="13" cy="22" r="2.6" fill="#D0D3DC"/>
        <circle cx="22" cy="22" r="2.6" fill="#D0D3DC"/>
        <circle cx="31" cy="22" r="2.6" fill="#D0D3DC"/>
      </svg>
    ),
    share: (
      <svg width={SZ} height={SZ} viewBox="0 0 44 44" fill="none">
        <circle cx="22" cy="22" r="22" fill="#2A2E3D"/>
        <path d="M22 10 L22 28" stroke="#D0D3DC" strokeWidth="2.5" strokeLinecap="round"/>
        <path d="M15 17 L22 10 L29 17" stroke="#D0D3DC" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
        <path d="M15 24 L15 32 L29 32 L29 24" stroke="#D0D3DC" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
      </svg>
    ),
    viewMore: (
      <svg width={SZ} height={SZ} viewBox="0 0 44 44" fill="none">
        <circle cx="22" cy="22" r="22" fill="#2A2E3D"/>
        <polyline points="14,19 22,27 30,19" stroke="#D0D3DC" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
      </svg>
    ),
    addToHome: (
      <svg width={SZ} height={SZ} viewBox="0 0 44 44" fill="none">
        <rect width="44" height="44" rx="10" fill="#2A2E3D"/>
        <line x1="22" y1="12" x2="22" y2="32" stroke="#D0D3DC" strokeWidth="2.5" strokeLinecap="round"/>
        <line x1="12" y1="22" x2="32" y2="22" stroke="#D0D3DC" strokeWidth="2.5" strokeLinecap="round"/>
      </svg>
    ),
    add: (
      <svg width={52} height={SZ} viewBox="0 0 52 44" fill="none">
        <rect width="52" height="44" rx="10" fill="#3478F6"/>
        <text x="26" y="28" textAnchor="middle" fill="#fff"
          style={{ fontSize: 15, fontWeight: 700, fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif" }}>
          Add
        </text>
      </svg>
    ),
  };

  const steps = [
    { icon: icons.dots,      label: "Tap ···" },
    { icon: icons.share,     label: "Share" },
    { icon: icons.viewMore,  label: "View More" },
    { icon: icons.addToHome, label: "Add to Home" },
    { icon: icons.add,       label: "Tap Add" },
  ];

  return (
    <div style={{
      position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 2000,
      fontFamily: "'HelveticaNeueStd','Barlow','Helvetica Neue',Arial,sans-serif",
      animation: "sheetSlideUp 0.62s cubic-bezier(0.32,0.72,0,1)",
    }}>
      <div onClick={handleDismiss} style={{ position: "fixed", inset: 0, zIndex: -1 }}/>

      <div style={{
        background: "#13161F",
        borderTop: "1px solid #2A2E3D",
        borderRadius: "20px 20px 0 0",
        boxShadow: "0 -12px 48px rgba(0,0,0,0.6)",
        padding: "14px 20px 44px",
        position: "relative",
      }}>
        <div style={{ width: 32, height: 3, borderRadius: 2,
          background: SA.borderGray, margin: "0 auto 16px" }}/>

        <Button variant="icon" onClick={handleDismiss} style={{
          position: "absolute", top: 16, right: 16,
          background: "#1E2130",
          width: 28, height: 28, flexShrink: 0,
          color: "#555E6E", fontSize: TYPOGRAPHY.note,
        }}>✕</Button>

        <div style={{ marginBottom: 16 }}>
          <span style={{
            fontSize: TYPOGRAPHY.tiny, fontWeight: 600, letterSpacing: "0.12em",
            textTransform: "uppercase", color: "#555E6E",
            background: "#1E2130", border: "1px solid #2A2E3D",
            borderRadius: RADIUS.pill, padding: "3px 10px",
          }}>Optional</span>
        </div>

        <div style={{ display: "flex", alignItems: "center", marginBottom: 20 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 13 }}>
            <img src={saLogoData} alt="Star Alliance" style={{
              width: 48, height: 48, borderRadius: 11,
              background: SA.black, padding: 5,
              border: "1px solid #2A2E3D", flexShrink: 0,
            }}/>
            <div>
              <div style={{ fontSize: 16, fontWeight: 700, color: "#F0F2F5",
                letterSpacing: "-0.02em", lineHeight: 1.2 }}>
                LAX Connect
              </div>
              <div style={{ fontSize: TYPOGRAPHY.body, color: "#6B7280", marginTop: 4,
                lineHeight: 1.4, maxWidth: 220 }}>
                Add to device as an app to enable push notifications
              </div>
            </div>
          </div>
        </div>

        <div style={{ height: 1, background: "#1E2130", marginBottom: 22 }}/>

        <div style={{ display: "flex", alignItems: "flex-start",
          justifyContent: "space-between", position: "relative" }}>
          <div style={{
            position: "absolute",
            top: SZ / 2, left: "10%", right: "10%",
            height: 1, background: "#1E2130", zIndex: 0,
          }}/>
          {steps.map(({ icon, label }, i) => (
            <div key={i} style={{ display: "flex", flexDirection: "column",
              alignItems: "center", gap: 8, flex: 1, zIndex: 1 }}>
              {icon}
              <div style={{ fontSize: TYPOGRAPHY.label, color: "#6B7280", textAlign: "center",
                letterSpacing: "0.01em", lineHeight: 1.3 }}>
                {label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
